import { Gauge, TrendingDown, TrendingUp } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';

import { ErrorNotice, PanelCard, SkeletonLines, StatRow } from './primitives';
import { Progress } from '@/components/ui/progress';
import {
  CHAINS,
  formatBlockTime,
  formatDateTime,
  formatDuration,
  formatInt,
  type ChainId,
  type DifficultyAdjustment,
} from '@/lib/mempool';

export function DifficultyCard({
  chainId,
  query,
}: {
  chainId: ChainId;
  query: UseQueryResult<DifficultyAdjustment>;
}) {
  const chain = CHAINS[chainId];
  const { data, isPending, isError, error } = query;

  const change = data?.difficultyChange ?? 0;
  const rising = change >= 0;

  return (
    <PanelCard
      icon={<Gauge className={`size-4 ${chain.classes.text}`} />}
      title="Difficulty Adjustment"
    >
      {isPending ? (
        <SkeletonLines rows={4} />
      ) : isError ? (
        <ErrorNotice message={error.message} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm text-muted-foreground">Estimated change</span>
            <span
              className={`flex items-center gap-1.5 font-mono text-2xl font-semibold tracking-tight ${
                rising
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-500 dark:text-red-400'
              }`}
            >
              {rising ? <TrendingUp className="size-5" /> : <TrendingDown className="size-5" />}
              {rising ? '+' : ''}
              {change.toFixed(2)}%
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline justify-between text-xs text-muted-foreground">
              <span>Epoch progress</span>
              <span className="font-mono">{data.progressPercent.toFixed(1)}%</span>
            </div>
            <Progress value={data.progressPercent} className={chain.classes.progressIndicator} />
            <div className="flex items-baseline justify-between text-xs text-muted-foreground">
              <span className="font-mono">{formatInt(2016 - data.remainingBlocks)} / 2,016 blocks</span>
              <span>
                retarget at height <span className="font-mono">{formatInt(data.nextRetargetHeight)}</span>
              </span>
            </div>
          </div>

          <div className="space-y-2 border-t pt-3">
            <StatRow
              label="Blocks remaining"
              value={formatInt(data.remainingBlocks)}
              sub={`≈ ${formatDuration(data.remainingTime)}`}
            />
            <StatRow label="Estimated retarget" value={formatDateTime(data.estimatedRetargetDate)} />
            <StatRow
              label="Avg block time (epoch)"
              value={formatBlockTime(data.adjustedTimeAvg ?? data.timeAvg)}
            />
            <StatRow
              label="Previous adjustment"
              value={`${data.previousRetarget >= 0 ? '+' : ''}${data.previousRetarget.toFixed(2)}%`}
            />
          </div>
        </div>
      )}
    </PanelCard>
  );
}
